import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import Navbar from '../components/Navbar';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const GENDERS = ['Male', 'Female', 'Other', 'Prefer not to say'];

// Email validation regex
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

function Profile({ user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const incomplete = searchParams.get('incomplete') === 'true';

  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    date_of_birth: user?.date_of_birth || '',
    gender: user?.gender || '',
    phone: user?.phone || ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const token = useMemo(() => localStorage.getItem('token'), []);
  const headers = useMemo(() => ({
    Authorization: `Bearer ${token}`
  }), [token]);

  // Determine which fields are missing when incomplete flag is set
  const missingFields = incomplete ? {
    phone: !formData.phone || formData.phone === '',
    date_of_birth: !formData.date_of_birth,
    gender: !formData.gender
  } : {};

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    // Check if user has a self-patient (their own patient record)
    // If yes, redirect to patient edit page instead of showing separate profile
    const checkSelfPatient = async () => {
      try {
        const response = await api.get(`${API_URL}/patients`, { headers });
        const patients = response.data.patients || [];
        const selfPatient = patients.find(p => p.relationship_to_admin === 'self');
        if (selfPatient) {
          // Redirect to patient edit page
          navigate(`/patients/${selfPatient.patient_id}/edit`);
        }
      } catch (err) {
        // If error fetching patients, just stay on profile page (user may have no patients)
        console.log('No patients found or error:', err.message);
      }
    };

    checkSelfPatient();
  }, [user, navigate, headers]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    setError('');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validate email format (even though it's read-only, ensure it's valid)
    if (!formData.email.trim()) {
      setError('Email is required');
      setLoading(false);
      return;
    }
    if (!isValidEmail(formData.email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      setError('First name and last name are required');
      setLoading(false);
      return;
    }

    // Validate phone: must be in format + followed by 10-15 digits
    if (formData.phone && !/^\+\d{10,15}$/.test(formData.phone)) {
      setError('Please enter phone number with country code e.g. +919876543210');
      setLoading(false);
      return;
    }

    try {
      await api.put(`${API_URL}/auth/profile`, {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        phone: formData.phone.trim(),
        date_of_birth: formData.date_of_birth || null,
        gender: formData.gender || null
      }, { headers });

      // Update local storage with new user data
      const updatedUser = { ...user, ...formData };
      localStorage.setItem('user', JSON.stringify(updatedUser));

      setSuccess('Profile updated successfully!');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  if (!user) {
    return null;
  }

  const age = calculateAge(user.date_of_birth);

  return (
    <div className="dashboard">
      <Navbar user={user} onLogout={handleLogout} />

      <div className="dashboard-content">
        <div className="page-header" style={{ marginBottom: '24px' }}>
          <Link to="/dashboard" style={{ color: '#2b6cb0', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', marginBottom: '8px' }}>
            ← Back to Dashboard
          </Link>
          <h2 style={{ color: '#2d3748' }}>My Profile</h2>
        </div>

        {error && <div className="api-error">{error}</div>}
        {success && <div className="success-message" style={{ backgroundColor: '#c6f6d5', color: '#22543d', padding: '12px', borderRadius: '8px', marginBottom: '20px' }}>{success}</div>}

        {incomplete && (
          <div style={{
            backgroundColor: '#fffaf0',
            border: '1px solid #fbd38d',
            color: '#744210',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>⚠️ Please complete your profile to access all features of Health Records Vault</span>
            <button
              onClick={() => window.location.href = '/profile'}
              style={{
                background: 'transparent',
                border: '1px solid #fbd38d',
                color: '#744210',
                padding: '4px 12px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              Skip for now
            </button>
          </div>
        )}

        <div className="profile-container" style={{ background: 'white', padding: '32px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', maxWidth: '600px' }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div
              className="profile-avatar"
              style={{
                width: '100px',
                height: '100px',
                borderRadius: '50%',
                backgroundColor: '#2b6cb0',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '2.5rem',
                fontWeight: '600',
                margin: '0 auto 16px'
              }}
            >
              {user.first_name?.[0]}{user.last_name?.[0]}
            </div>
            <h3 style={{ color: '#2d3748', marginBottom: '4px' }}>
              {user.first_name} {user.last_name}
              {age && <span style={{ fontSize: '1rem', color: '#718096', fontWeight: 'normal', marginLeft: '8px' }}>({age}{user.gender?.[0] || ''})</span>}
            </h3>
            <p style={{ color: '#718096' }}>{user.email}</p>
            <p style={{ color: '#718096', fontSize: '0.9rem', marginTop: '4px' }}>
              Role: <span style={{ textTransform: 'capitalize' }}>{user.role}</span>
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="first_name" style={{ display: 'block', marginBottom: '6px', color: '#4a5568', fontWeight: '500' }}>First Name *</label>
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  placeholder="Enter first name"
                  style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '1rem' }}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="last_name" style={{ display: 'block', marginBottom: '6px', color: '#4a5568', fontWeight: '500' }}>Last Name *</label>
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  placeholder="Enter last name"
                  style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '1rem' }}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="date_of_birth" style={{ display: 'block', marginBottom: '6px', color: '#4a5568', fontWeight: '500' }}>Date of Birth</label>
                <input
                  type="date"
                  id="date_of_birth"
                  name="date_of_birth"
                  value={formData.date_of_birth}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: missingFields.date_of_birth ? '2px solid #ed8936' : '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div className="form-group">
                <label htmlFor="gender" style={{ display: 'block', marginBottom: '6px', color: '#4a5568', fontWeight: '500' }}>Gender</label>
                <select
                  id="gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: missingFields.gender ? '2px solid #ed8936' : '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem'
                  }}
                >
                  <option value="">Select gender</option>
                  {GENDERS.map(g => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: '24px' }}>
              <label htmlFor="email" style={{ display: 'block', marginBottom: '6px', color: '#4a5568', fontWeight: '500' }}>Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter email"
                style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '1rem', backgroundColor: '#f7fafc', color: '#718096' }}
                disabled
              />
              <p style={{ fontSize: '0.85rem', color: '#718096', marginTop: '4px' }}>
                Email cannot be changed. Contact support if you need to update it.
              </p>
            </div>

            <div className="form-group" style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '6px', color: '#4a5568', fontWeight: '500' }}>Phone</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+91XXXXXXXXXX"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: missingFields.phone ? '2px solid #ed8936' : '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="submit"
                className="submit-btn"
                disabled={loading}
                style={{ padding: '12px 32px' }}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
              <Link
                to="/dashboard"
                style={{
                  padding: '12px 32px',
                  backgroundColor: '#cbd5e0',
                  color: '#4a5568',
                  textDecoration: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600'
                }}
              >
                Cancel
              </Link>
            </div>
          </form>
        </div>

      </div>
    </div>
  );
}

export default Profile;
